import './pageContent.scss';

import { Button, Popup as PopupOld, SearchIcon, TransparentButton } from '@approvalmax/ui';
import { Button as ButtonRedesign, Popup, SplitView } from '@approvalmax/ui/src/components';
import { dateTimeHelpers, hooks } from '@approvalmax/utils';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectors } from 'modules/common';
import { getIntegrationById } from 'modules/common/selectors/integrationSelectors';
import ImageSleep from 'modules/components/ImageIcons/ImageSleep';
import ImageSun from 'modules/components/ImageIcons/ImageSun';
import ImageSunglasses from 'modules/components/ImageIcons/ImageSunglasses';
import { domain, State } from 'modules/data';
import { DefaultPageLayout } from 'modules/page';
import { useSelector, useShallowEqualSelector } from 'modules/react-redux';
import * as requestModule from 'modules/request';
import { SearchBigIcon, SearchWhiteIcon } from 'modules/sprites';
import { useUserProfile } from 'modules/utils';
import { EditReviewersPopup } from 'pages/requestList/containers/EditReviewersPopup/EditReviewersPopup';
import { EDIT_REVIEWERS_POPUP } from 'pages/requestList/reducers/page/activePopup/editReviewersPopupReducer';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import bemFactory from 'react-bem-factory';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { amplitudeService } from 'services/amplitude';
import { routingService } from 'services/routing';
import {
    ComponentErrorBoundary,
    FileMenuDropdown,
    slotHeaderState,
    SplitViewGoToBar,
    splitViewGoToBarSettingsState,
} from 'shared/components';
import { useClearSplitViewContentOnClose } from 'shared/hooks/useClearSplitViewContentOnClose';
import { useCloseSplitViewOnUnmount } from 'shared/hooks/useCloseSplitViewOnUnmount';
import { useSplitViewContent } from 'shared/hooks/useSplitViewContent';
import { ContentSplitViewMode, contentSplitViewState } from 'shared/states';
import { getPath, Path } from 'urlBuilder';

import { cancelActivePopup, showRequestSearchPopup } from '../../actions';
import {
    CompanyRequestListFilter,
    RequestListFilter,
    RequestListFilterMeta,
    TemplateRequestListFilter,
} from '../../config';
import CancelRequestPopup from '../../containers/CancelRequestPopup';
import EditParticipantsPopup from '../../containers/EditParticipantsPopup';
import ForceDecisionPopup from '../../containers/ForceDecisionPopup';
import OperationResultPopup from '../../containers/OperationResultPopup';
import { RejectRequestPopup } from '../../containers/RejectRequestPopup';
import { RequestList } from '../../containers/RequestList';
import type { NavigateToRequest } from '../../containers/RequestList.types';
import RequestReassignPopup from '../../containers/RequestReassignPopup';
import RequestSearchPopup from '../../containers/RequestSearchPopup/RequestSearchPopup';
import RevokeDecisionPopup from '../../containers/RevokeDecisionPopup';
import StartOverPopup from '../../containers/StartOverPopup';
import DeclineCustomerDecisionPopup from '../../containers/xero/CustomerDecisionSection/components/DeclineCustomerDecisionPopup';
import { useRequestsListBreadcrumbs } from '../../hooks/useBreadcrumbs';
import useMakeDecision from '../../hooks/useMakeDecision';
import { CANCEL_REQUEST_POPUP } from '../../reducers/page/activePopup/cancelRequestPopupReducer';
import { DECLINE_CUSTOMER_DECISION_POPUP } from '../../reducers/page/activePopup/declineCustomerDecisionPopupReducer';
import { EDIT_PARTICIPANTS_POPUP } from '../../reducers/page/activePopup/editParticipantsPopupReducer';
import { EDIT_WATCHERS_POPUP } from '../../reducers/page/activePopup/editWatchersPopupReducer';
import { FORCE_DECISION_POPUP } from '../../reducers/page/activePopup/forceDecisionPopupReducer';
import { OPERATION_RESULT_POPUP } from '../../reducers/page/activePopup/operationResultPopupReducer';
import { REJECT_REQUEST_POPUP } from '../../reducers/page/activePopup/rejectRequestPopupReducer';
import { REQUEST_REASSIGN_POPUP } from '../../reducers/page/activePopup/requestReassignPopupReducer';
import { REQUEST_SEARCH_POPUP } from '../../reducers/page/activePopup/requestSearchPopupReducer';
import { REVOKE_DECISION_POPUP } from '../../reducers/page/activePopup/revokeDecisionPopupReducer';
import { START_OVER_POPUP } from '../../reducers/page/activePopup/startOverPopupReducer';
import {
    getActiveFilter,
    getActivePopupId,
    getActiveRequestId,
    getActiveRequestSafe,
    getActiveTemplate,
    getActiveTemplateIntegrationCode,
    getActiveTemplateIsStandalone,
    getActiveTemplateName,
    getDocumentTitle,
    getFilterAmplitudeEventName,
    getSearchParams,
    isActiveTemplateIsCreatable,
    isEmptyList,
    isInSearchMode,
    isSearchingNow,
    isSearchNothingFound,
} from '../../selectors/pageSelectors';
import { EditWatchersPopupContent } from '../EditWatchersPopupContent/EditWatchersPopupContent';
import EditWatchersPopupOld from '../EditWatchersPopupOld/EditWatchersPopup';
import { RequestCard } from '../RequestCard/RequestCard';
import { LeftSplitViewItemContent } from './components/LeftSplitViewItemContent/LeftSplitViewItemContent';
import { RequestCardOverlay } from './components/RequestCardOverlay/RequestCardOverlay';
import { RequestVersionHistory } from './components/RequestVersionHistory/RequestVersionHistory';
import { StarterGuideEmptyPopupContent } from './components/StarterGuideEmptyPopupContent/StarterGuideEmptyPopupContent';
import {
    useOcrStatusHandler,
    useRequestVersionHistory,
    useSplitViewOnNextRequest,
    useStarterGuide,
} from './PageContent.hooks';
import { messages } from './PageContent.messages';
import { PageContentProps } from './PageContent.types';

const bem = bemFactory.block('reql-page');
const qa = bemFactory.qa('reql-page');
const qaSplitView = bemFactory.qa('reql-document-preview-popup');

export const PageContent: FC<PageContentProps> = memo((props) => {
    const { generateRequestUrl, requestListPage, openTour } = props;

    const { push } = useHistory();
    const location = useLocation();
    const searchRoute = useRouteMatch(getPath(Path.searchRequests));
    const { companyId } = useParams<{ companyId: string }>();
    const setSlotHeader = useSetAtom(slotHeaderState);
    const setContentSplitView = useSetAtom(contentSplitViewState);
    const [splitViewFirstItemTitle, setSplitViewFirstItemTitle] = useState('');
    const [splitViewSecondItemTitle, setSplitViewSecondItemTitle] = useState('');
    const splitViewGoToBarSettings = useAtomValue(splitViewGoToBarSettingsState);
    const [, setRequestId] = hooks.useQueryParam('requestId');

    const request = useSelector(getActiveRequestSafe);
    const profile = useUserProfile();

    const {
        attachments,
        activeAttachment,
        auditReportStatus,
        goToNextDocument,
        goToPrevDocument,
        attachmentIndex,
        canDownload,
        hasPaginator,
        activeRequestVersion,
        isVersionHistoryPreviewMode,
    } = useSplitViewContent();

    useRequestsListBreadcrumbs(requestListPage);
    useClearSplitViewContentOnClose();
    useCloseSplitViewOnUnmount();
    useSplitViewOnNextRequest(request);

    const { modifiedRequest, setModifiedRequest, setSelectedVersion, selectedVersion } = useRequestVersionHistory({
        companyId,
        request,
    });

    const dispatch = useDispatch();
    const {
        documentTitle,
        emptyList,
        searchingNow,
        searchNothingFound,
        inSearchMode,
        activeRequestId,
        activePopupId,
        activeTemplateIntegrationCode,
        filter,
        templateIsStandalone,
        activeTemplateName,
        activeTemplateIsCreatable,
        searchParams,
        activeCompanyId,
        template,
        company,
        companyIntegration,
    } = useShallowEqualSelector((state: State) => {
        const template = getActiveTemplate(state);
        const company = companyId ? selectors.company.getCompanyById(state, companyId) : null;
        const companyIntegration = company?.integrationId ? getIntegrationById(state, company?.integrationId) : null;

        return {
            me: selectors.profile.getProfileUser(state),
            documentTitle: getDocumentTitle(state),
            emptyList: isEmptyList(state),
            searchingNow: isSearchingNow(state),
            searchNothingFound: isSearchNothingFound(state),
            inSearchMode: isInSearchMode(state),
            searchParams: getSearchParams(state),
            filter: getActiveFilter(state),
            activeRequestId: getActiveRequestId(state),
            activePopupId: getActivePopupId(state),
            activeTemplateName: getActiveTemplateName(state),
            activeTemplateIsCreatable: isActiveTemplateIsCreatable(state),
            activeCompanyId: selectors.navigation.getActiveCompanyId(state),
            templateIsStandalone: getActiveTemplateIsStandalone(state),
            activeTemplateIntegrationCode: getActiveTemplateIntegrationCode(state),
            template,
            company,
            companyIntegration,
        };
    });

    useMakeDecision();

    useOcrStatusHandler(activeRequestId);

    const navigateToRequest = useCallback<NavigateToRequest>(
        (activeRequestId, companyId) => {
            if (!activeRequestId || !companyId) {
                push({ pathname: location.pathname });

                return;
            }

            if (generateRequestUrl) {
                routingService.push(generateRequestUrl(activeRequestId, companyId));
            } else {
                setRequestId(activeRequestId);
            }
        },
        [generateRequestUrl, push, location.pathname, setRequestId]
    );

    useEffect(() => {
        if (emptyList) {
            push({ pathname: location.pathname });
        }
    }, [emptyList, push, location.pathname]);

    useEffect(() => {
        let filterProperty = getFilterAmplitudeEventName(filter);
        let listProperty = filterProperty?.toLocaleLowerCase();

        if (activeTemplateIntegrationCode) {
            listProperty = `${activeTemplateIntegrationCode.toLocaleLowerCase()} ${listProperty}`;
        }

        amplitudeService.sendData('requests: viewed list', {
            list: listProperty,
        });
    }, [filter, activeTemplateIntegrationCode]);

    const { isOpenStarterGuide, onToggleStarterGuide, onCloseStarterGuide } = useStarterGuide({
        isEmptyList: emptyList,
        profileId: profile.id,
        companyId,
        openTour,
    });

    const onNewRequest = () => {
        if (
            filter &&
            activeCompanyId &&
            !(filter in RequestListFilter) &&
            !(companyIntegration?.status === domain.IntegrationStatus.Disconnected)
        ) {
            dispatch(requestModule.loadActiveCompanyNewRequestPopup(activeCompanyId));
        } else {
            dispatch(requestModule.loadNewRequestPopup());
        }
    };

    const onSetupWorkflow = useCallback(() => {
        if (!company) return;

        const to = company.flags.hasActiveIntegration
            ? getPath(Path.workflow, company.id, template?.id)
            : getPath(Path.companyWorkflows, company.id);

        push(to);
    }, [company, push, template?.id]);

    const renderEmptyContent = () => {
        let title, text, icon;

        switch (filter) {
            case CompanyRequestListFilter.CompanyMyDecisionRequired:
            case RequestListFilter.MyDecisionRequired:
                title = messages.myDecisionRequiredEmptyTitle;
                text = messages.myDecisionRequiredEmptyText;
                icon = <ImageSleep />;
                break;

            case CompanyRequestListFilter.CompanyMyReviewRequired:
            case RequestListFilter.MyReviewRequired:
                title = messages.myReviewRequiredEmptyTitle;
                text = messages.myDecisionRequiredEmptyText;
                icon = <ImageSleep />;
                break;

            case RequestListFilter.MyOnlyOpen:
                title = messages.myOpenEmptyTitle;
                icon = <ImageSun />;
                break;

            case CompanyRequestListFilter.CompanyMyOpen:
            case CompanyRequestListFilter.CompanyAllOpen:
                title = messages.companyMyOpenEmptyTitle;
                icon = <ImageSun />;
                break;

            case RequestListFilter.MyDraft:
                title = messages.myDraftEmptyTitle;
                icon = <ImageSleep />;
                break;

            case CompanyRequestListFilter.CompanyAll:
                title = messages.companyAllEmptyTitle;
                icon = <ImageSunglasses />;
                break;

            case TemplateRequestListFilter.OnApproval:
            case TemplateRequestListFilter.OnReview:
                title = messages.onStatusEmptyTitle({
                    templateName: templateIsStandalone ? messages.requests : activeTemplateName,
                    filterName: RequestListFilterMeta[filter]?.title ?? filter,
                });
                icon = <ImageSleep />;
                break;

            case TemplateRequestListFilter.Approved:
            case TemplateRequestListFilter.Cancelled:
            case TemplateRequestListFilter.Rejected:
            case TemplateRequestListFilter.Paid:
            case TemplateRequestListFilter.Billed:
            case TemplateRequestListFilter.Accepted:
            case TemplateRequestListFilter.Declined:
            case TemplateRequestListFilter.NotBilled:
                title = messages.templateRequestEmptyTitle({
                    templateName: templateIsStandalone ? messages.requests : activeTemplateName,
                    filterName: RequestListFilterMeta[filter]?.title ?? filter,
                });
                icon = <ImageSleep />;
                break;

            default:
                title = messages.defaultEmptyTitle;
                icon = <ImageSleep />;
        }

        return (
            <div className={bem('empty-content')} data-qa={qa('empty-content')}>
                <div className={bem('empty-content-icon')}>{icon}</div>

                <div className={bem('empty-content-title')}>{title}</div>

                <div className={bem('empty-content-text')}>{text}</div>

                {template?.enabled
                    ? activeTemplateIsCreatable && (
                          <Button
                              qa={qa('new-request-button')}
                              title={messages.newRequestButtonTitle}
                              execute={onNewRequest}
                          >
                              {messages.newRequestButtonText}
                          </Button>
                      )
                    : template && <Button execute={onSetupWorkflow}>{messages.setupNewWorkflowButtonText}</Button>}
            </div>
        );
    };

    const renderSearchingNowContent = () => {
        return (
            <div className={bem('searching-now-content')} data-qa={qa('searching-now-content')}>
                <div className={bem('searching-now-text')}>{messages.searchingNowText}</div>
            </div>
        );
    };

    const renderSearchNotFoundContent = () => {
        return (
            <div className={bem('search-not-found-content')} data-qa={qa('search-not-found-content')}>
                <div className={bem('search-not-found-panel')}>
                    <SearchBigIcon width={48} height={48} />

                    <div className={bem('search-not-found-text')}>{messages.searchNotFoundText}</div>

                    <TransparentButton
                        className={bem('search-not-found-button')}
                        execute={() => dispatch(showRequestSearchPopup(searchParams))}
                    >
                        <SearchWhiteIcon width={15} height={15} />

                        {messages.changeSearchButtonText}
                    </TransparentButton>
                </div>
            </div>
        );
    };

    let content;

    if ((template && !template.enabled) || emptyList) {
        content = renderEmptyContent();
    } else if (searchingNow) {
        content = renderSearchingNowContent();
    } else if (searchNothingFound) {
        content = renderSearchNotFoundContent();
    } else {
        content = (
            <div className={bem('content')}>
                <RequestList className={bem('list')} navigateToRequest={navigateToRequest} />

                <div className={bem('card-wrp')}>
                    <RequestCard className={bem('card')} request={request} />

                    <RequestCardOverlay activeRequestId={activeRequestId} />
                </div>
            </div>
        );
    }

    const openSearchFilters = useCallback(
        () => dispatch(showRequestSearchPopup(searchParams)),
        [dispatch, searchParams]
    );

    useEffect(() => {
        if (!inSearchMode) return;

        setSlotHeader(
            <ButtonRedesign onClick={openSearchFilters} size='small' color='green80' startIcon={<SearchIcon />}>
                {messages.changeSearchButtonText}
            </ButtonRedesign>
        );

        return () => {
            if (!searchRoute) {
                setSlotHeader(null);
            }
        };
    }, [inSearchMode, openSearchFilters, searchRoute, setSlotHeader]);

    const handleCancelActivePopup = useCallback(() => {
        dispatch(cancelActivePopup());
    }, [dispatch]);

    const onCloseSplitView = useCallback(() => {
        setSplitViewFirstItemTitle('');
        setContentSplitView({ mode: ContentSplitViewMode.Empty });
        setSelectedVersion(null);
        setModifiedRequest(null);
    }, [setModifiedRequest, setSelectedVersion, setContentSplitView]);

    const handleWatchersPopupToggle = useCallback(
        (open: boolean) => {
            if (!open) {
                handleCancelActivePopup();
            }
        },
        [handleCancelActivePopup]
    );

    const isDextRequest = request?.origin === domain.RequestOrigin.ReceiptBank;

    const showEditWatchersPopupRedesign = profile.betaFeatures.includes(domain.ProfileBetaFeature.BelgradeUIWatchers);

    return (
        <DefaultPageLayout documentTitle={documentTitle}>
            {(filter === CompanyRequestListFilter.CompanyMyDecisionRequired ||
                filter === RequestListFilter.MyDecisionRequired) && (
                <div className={bem('starter-guide')} data-starter-guide='decision_required_approve_document' />
            )}

            {content}

            <SplitView className='fs-mask' data-qa={qaSplitView()}>
                <SplitView.Item
                    showInFullScreen
                    title={
                        <FileMenuDropdown
                            title={splitViewFirstItemTitle}
                            attachments={attachments}
                            request={request}
                            showAddButton={false}
                        />
                    }
                    onClose={onCloseSplitView}
                    customToolbar={
                        splitViewGoToBarSettings.show && activeAttachment ? (
                            <SplitViewGoToBar
                                downloadButtonProps={{ url: activeAttachment?.url || '' }}
                                paginationProps={{
                                    currentIndex: (attachmentIndex || 0) + 1,
                                    total: attachments.length,
                                    goToNextDocument,
                                    goToPrevDocument,
                                }}
                                qa={qaSplitView}
                            />
                        ) : undefined
                    }
                    actions={
                        activeAttachment ? (
                            <>
                                {canDownload && (
                                    <SplitView.Addons.DownloadButton
                                        url={activeAttachment?.url || ''}
                                        qa={qaSplitView}
                                    />
                                )}

                                {hasPaginator && (
                                    <SplitView.Addons.Pagination
                                        currentIndex={(attachmentIndex || 0) + 1}
                                        total={attachments.length}
                                        goToNextDocument={goToNextDocument}
                                        goToPrevDocument={goToPrevDocument}
                                        qa={qaSplitView}
                                    />
                                )}
                            </>
                        ) : null
                    }
                    preferredSize={isVersionHistoryPreviewMode ? 560 : undefined}
                    qa={qaSplitView}
                >
                    {isVersionHistoryPreviewMode && request ? (
                        <RequestVersionHistory
                            onSelectVersion={setSelectedVersion}
                            selectedVersion={selectedVersion}
                            requestId={request.id}
                            companyId={companyId}
                            setSplitViewFirstItemTitle={setSplitViewFirstItemTitle}
                            isDextRequest={isDextRequest}
                        />
                    ) : (
                        <ComponentErrorBoundary>
                            <LeftSplitViewItemContent
                                activeAttachment={activeAttachment}
                                auditReportStatus={auditReportStatus}
                                goToNextDocument={goToNextDocument}
                                goToPrevDocument={goToPrevDocument}
                                splitViewFirstItemTitle={splitViewFirstItemTitle}
                                setSplitViewFirstItemTitle={setSplitViewFirstItemTitle}
                            />
                        </ComponentErrorBoundary>
                    )}
                </SplitView.Item>

                <SplitView.Item
                    title={
                        selectedVersion
                            ? messages.requestVersionTitle({
                                  version: selectedVersion.localVersionNumber,
                                  date: dateTimeHelpers.formatDateTime(selectedVersion.createdAt, 'DateAndTime'),
                              })
                            : splitViewSecondItemTitle
                    }
                    qa={qaSplitView}
                    hideToggleFullscreen={isVersionHistoryPreviewMode}
                >
                    <RequestCard
                        setContainerTitle={setSplitViewSecondItemTitle}
                        request={
                            modifiedRequest && activeRequestVersion !== selectedVersion?.version
                                ? modifiedRequest
                                : request
                        }
                        readOnly={Boolean(selectedVersion) && activeRequestVersion !== selectedVersion?.version}
                    />
                </SplitView.Item>
            </SplitView>

            <PopupOld isOpen={activePopupId === REJECT_REQUEST_POPUP} onRequestClose={handleCancelActivePopup}>
                <RejectRequestPopup />
            </PopupOld>

            <PopupOld
                isOpen={activePopupId === DECLINE_CUSTOMER_DECISION_POPUP}
                onRequestClose={handleCancelActivePopup}
            >
                <DeclineCustomerDecisionPopup />
            </PopupOld>

            <PopupOld isOpen={activePopupId === FORCE_DECISION_POPUP} onRequestClose={handleCancelActivePopup}>
                <ForceDecisionPopup />
            </PopupOld>

            <PopupOld isOpen={activePopupId === OPERATION_RESULT_POPUP} onRequestClose={handleCancelActivePopup}>
                <OperationResultPopup />
            </PopupOld>

            <PopupOld isOpen={activePopupId === CANCEL_REQUEST_POPUP} onRequestClose={handleCancelActivePopup}>
                <CancelRequestPopup />
            </PopupOld>

            <PopupOld isOpen={activePopupId === START_OVER_POPUP} onRequestClose={handleCancelActivePopup}>
                <StartOverPopup />
            </PopupOld>

            <PopupOld isOpen={activePopupId === REVOKE_DECISION_POPUP} onRequestClose={handleCancelActivePopup}>
                <RevokeDecisionPopup />
            </PopupOld>

            <PopupOld
                isOpen={activePopupId === EDIT_PARTICIPANTS_POPUP}
                disableAutoClose
                onRequestClose={handleCancelActivePopup}
            >
                <EditParticipantsPopup />
            </PopupOld>

            <PopupOld
                isOpen={activePopupId === EDIT_REVIEWERS_POPUP}
                disableAutoClose
                onRequestClose={handleCancelActivePopup}
            >
                <EditReviewersPopup />
            </PopupOld>

            {showEditWatchersPopupRedesign ? (
                <Popup
                    open={activePopupId === EDIT_WATCHERS_POPUP}
                    preventAutoClose
                    onToggle={handleWatchersPopupToggle}
                >
                    <EditWatchersPopupContent />
                </Popup>
            ) : (
                <PopupOld
                    isOpen={activePopupId === EDIT_WATCHERS_POPUP}
                    disableAutoClose
                    onRequestClose={handleCancelActivePopup}
                >
                    <EditWatchersPopupOld />
                </PopupOld>
            )}

            <PopupOld isOpen={activePopupId === REQUEST_REASSIGN_POPUP} onRequestClose={handleCancelActivePopup}>
                <RequestReassignPopup />
            </PopupOld>

            <PopupOld isOpen={activePopupId === REQUEST_SEARCH_POPUP} onRequestClose={handleCancelActivePopup}>
                <RequestSearchPopup />
            </PopupOld>

            <Popup open={isOpenStarterGuide} onToggle={onToggleStarterGuide} size='xsmall'>
                <StarterGuideEmptyPopupContent companyId={companyId} onClose={onCloseStarterGuide} />
            </Popup>
        </DefaultPageLayout>
    );
});

PageContent.displayName = 'PageContent';
